'use client';

/**
 * About Page
 * Highlighting cultural authenticity and business connections
 */

import Image from 'next/image';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import {
  Heart,
  Users,
  Zap,
  TrendingUp,
  Globe,
  Award,
  ArrowRight,
} from 'lucide-react';
import Link from 'next/link';
import { brandMetrics } from '@/lib/data';

export default function AboutPage() {
  const values = [
    {
      icon: Heart,
      title: 'Cultural Authenticity',
      description:
        'We create experiences rooted in genuine cultural moments, not manufactured hype. Every activation tells a real story.',
    },
    {
      icon: Users,
      title: 'Business Connections',
      description:
        'Leveraging deep relationships with brands, venues, and cultural leaders to create unprecedented partnerships.',
    },
    {
      icon: Zap,
      title: 'Revenue-Driven',
      description:
        'Beautiful activations that also deliver ROI. We measure success in brand lift, sales, and lasting impact.',
    },
    {
      icon: Globe,
      title: 'World-Class Events',
      description:
        'From intimate pop-ups to massive World Cup 2026 activations, we deliver experiences at any scale.',
    },
  ];

  const achievements = [
    {
      icon: TrendingUp,
      number: brandMetrics.avgBrandLift,
      label: 'Average Brand Awareness Lift',
    },
    {
      icon: Users,
      number: `${(brandMetrics.totalAttendees / 1000).toFixed(0)}K+`,
      label: 'Total Attendees Across Activations',
    },
    {
      icon: Globe,
      number: brandMetrics.socialReach,
      label: 'Combined Social Media Reach',
    },
    {
      icon: Award,
      number: `${brandMetrics.totalActivations}+`,
      label: 'Successful Activations Delivered',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/images/generated/hero-experiential-activation-crowd.png"
            alt="Experiential activation"
            fill
            className="object-cover opacity-40"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-6xl md:text-8xl font-black mb-6 uppercase leading-none">
              <span className="text-gradient-electric block">We Create</span>
              <span className="text-gradient-sunset block">Culture</span>
            </h1>
            <p className="text-xl md:text-2xl text-foreground/80">
              The Audax Group is Atlanta's premier experiential retail agency,
              specializing in bold activations that connect brands with culture and
              consumers with unforgettable moments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black mb-6 uppercase">
                Our <span className="text-gradient-electric">Mission</span>
              </h2>
              <p className="text-xl text-foreground/80">
                To create revenue-driven experiential activations that amplify brand
                awareness while staying true to cultural authenticity. We don't just
                throw parties—we create movements that drive real business impact.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase">
              What Sets Us <span className="text-gradient-sunset">Apart</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 h-full card-hover border-2 border-transparent hover:border-primary">
                  <value.icon className="w-12 h-12 text-primary mb-4" />
                  <h3 className="text-2xl font-black mb-3 uppercase">
                    {value.title}
                  </h3>
                  <p className="text-foreground/80">{value.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-24 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase">
              Proven <span className="text-gradient-electric">Results</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Numbers that demonstrate our commitment to measurable brand impact
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 text-center">
                  <achievement.icon className="w-12 h-12 text-primary mx-auto mb-4" />
                  <div className="text-5xl font-black text-gradient-electric mb-2">
                    {achievement.number}
                  </div>
                  <p className="text-sm text-muted-foreground font-bold">
                    {achievement.label}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* World Cup 2026 */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="relative overflow-hidden bg-gradient-atlanta p-12 md:p-16">
              <div className="absolute inset-0 opacity-10">
                <Image
                  src="/images/generated/activation-atlanta-soccer-fan-zone.png"
                  alt="World Cup"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="relative z-10 text-center">
                <h2 className="text-4xl md:text-6xl font-black mb-6 text-white uppercase">
                  World Cup 2026 Partner
                </h2>
                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  As Atlanta prepares to host World Cup 2026 matches, we're creating
                  unforgettable experiences that celebrate soccer culture and connect
                  fans from around the world.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    asChild
                    size="lg"
                    variant="secondary"
                    className="text-lg px-8 h-14 shadow-2xl"
                  >
                    <Link href="/events">
                      View Events
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="text-lg px-8 h-14 bg-white/10 border-white text-white hover:bg-white hover:text-background"
                  >
                    <Link href="/shop">Shop Merch</Link>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6 uppercase">
              Let's Create <span className="text-gradient-sunset">Together</span>
            </h2>
            <p className="text-xl text-foreground/80 mb-8">
              Whether you're a brand looking to amplify awareness or a fan ready to
              experience culture, we're here for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8 h-14">
                <Link href="/partnerships">
                  Partner With Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="text-lg px-8 h-14"
              >
                <Link href="/events">Browse Events</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
